import type { App } from "vue";
import useAccessibilityStore from "@shared-stores/accessibility.store";
import { generateTtsId, setTtsDisabledHandler, speak } from "@shared-utils/accessibility/tts.utils";

// Map pour stocker le contenu original de chaque élément
const ttsOriginalContentMap = new WeakMap<HTMLElement, string | null>();

// Référence à l'élément en cours de lecture
let currentPlayingElement: HTMLElement | null = null;

// Flag pour éviter d'initialiser le listener global plusieurs fois
let globalListenerInitialized = false;

/**
 * Initialise le listener global pour arrêter la lecture au clic extérieur
 */
const initGlobalClickListener = () =>
{
	if (globalListenerInitialized)
	{
		return;
	}

	document.addEventListener("click", (e) =>
	{
		if (currentPlayingElement && ! currentPlayingElement.contains(e.target as Node))
		{
			// Clic en dehors de l'élément en lecture → arrêter
			if (currentPlayingElement.classList.contains("is-playing"))
			{
				const selector = "#" + currentPlayingElement.id;
				const uniqueId = currentPlayingElement.dataset.ttsId;
				const bubbleSelector = `[data-tts-id="${ uniqueId }"]`;
				const originalContent = ttsOriginalContentMap.get(currentPlayingElement) ?? null;

				// Appelle speak pour stopper (toggle off)
				speak(originalContent, selector, bubbleSelector);
				ttsOriginalContentMap.set(currentPlayingElement, null);
				currentPlayingElement = null;
			}
		}
	});

	globalListenerInitialized = true;
};

/**
 * Interrompt la lecture en cours et rend à l'élément son contenu d'origine.
 *
 * Lire remplace le texte par un balisage de surlignage : couper la voix sans le défaire
 * laisserait la phrase figée, mot courant encadré, jusqu'au prochain clic. Appelé quand le
 * lecteur vocal est éteint alors qu'il parlait.
 */
const stopTtsPlayback = (): void =>
{
	if (! currentPlayingElement)
	{
		return;
	}

	if (currentPlayingElement.classList.contains("is-playing"))
	{
		const selector = "#" + currentPlayingElement.id;
		const bubbleSelector = `[data-tts-id="${ currentPlayingElement.dataset.ttsId }"]`;

		speak(ttsOriginalContentMap.get(currentPlayingElement) ?? null, selector, bubbleSelector);
		ttsOriginalContentMap.set(currentPlayingElement, null);
	}

	currentPlayingElement = null;
};

/**
 * Installe la directive v-tts sur l'application Vue
 */
export const installTtsDirective = (app: App): void =>
{
	// Initialise le listener global
	initGlobalClickListener();

	// Le store coupe la voix quand on éteint le lecteur ; c'est ici qu'on sait rendre à
	// l'élément le texte que le surlignage lui avait pris.
	setTtsDisabledHandler(stopTtsPlayback);

	app.directive("tts", {
		mounted: (el: HTMLElement, binding: { value?: boolean | Record<string, boolean>; modifiers: Record<string, boolean> }) =>
		{
			const isObject = typeof binding.value === "object" && binding.value !== null;
			const enableTts = isObject ? ((binding.value as Record<string, boolean>).enabled ?? true) : (binding.value ?? true);
			const dynamicModifiers = isObject ? (binding.value as Record<string, boolean>) : {};
			const modifiers = { ...binding.modifiers, ...dynamicModifiers };

			if (! enableTts)
			{
				return;
			}

			const accessibilityStore = useAccessibilityStore();

			// Génère un ID unique pour cet élément
			const uniqueId = generateTtsId();
			el.id = el.id || uniqueId;
			el.dataset.ttsId = uniqueId;

			// Ajoute la classe pour le style
			el.classList.add("accessibility-tts-block");

			if (modifiers.bottom)
			{
				el.classList.add("accessibility-tts-block-bottom");
			}

			if (modifiers.inside)
			{
				el.classList.add("accessibility-tts-block-inside");
			}

			if (modifiers.right)
			{
				el.classList.add("accessibility-tts-block-right");
			}

			if (modifiers.center)
			{
				el.classList.add("accessibility-tts-block-center");
			}

			if (modifiers.fit)
			{
				el.classList.add("accessibility-tts-block-fit");
			}

			if (modifiers.above)
			{
				el.classList.add("accessibility-tts-block-above");
			}

			if (modifiers.left)
			{
				el.classList.add("accessibility-tts-block-left");
			}

			if (modifiers.rect)
			{
				el.classList.add("accessibility-tts-block-rect");
			}

			// Ré-affiche la bulle icône (masquée par défaut) sur des éléments précis.
			if (modifiers.bubble)
			{
				el.classList.add("accessibility-tts-block-bubble");
			}

			// Support pour modifier "light" via attribut data-tts-theme ou modifier statique
			const ttsTheme = el.getAttribute("data-tts-theme") || el.getAttribute("tts-theme");
			if (modifiers.light || ttsTheme === "light")
			{
				el.classList.add("accessibility-tts-block-light");
			}

			if (modifiers.force)
			{
				el.classList.add("accessibility-tts-block-force");
			}

			// Initialise le contenu original à null
			ttsOriginalContentMap.set(el, null);

			el.addEventListener("click", (e) =>
			{
				// Ne rien faire si le TTS n'est pas activé
				if (! accessibilityStore.tts)
				{
					return;
				}

				// Ignorer les clics sur des éléments interactifs (boutons, liens, inputs, etc.)
				const target = e.target as HTMLElement;
				const interactiveSelectors = "a, button, input, select, textarea, [role='button'], [role='link'], .btn, [onclick]";
				if (target.closest(interactiveSelectors) && target.closest(interactiveSelectors) !== el)
				{
					return;
				}

				// Empêche la propagation pour ne pas déclencher le listener global
				e.stopPropagation();

				// Si un autre élément est en lecture, l'arrêter d'abord
				if (currentPlayingElement && currentPlayingElement !== el && currentPlayingElement.classList.contains("is-playing"))
				{
					const prevSelector = "#" + currentPlayingElement.id;
					const prevUniqueId = currentPlayingElement.dataset.ttsId;
					const prevBubbleSelector = `[data-tts-id="${ prevUniqueId }"]`;
					const prevOriginalContent = ttsOriginalContentMap.get(currentPlayingElement) ?? null;
					speak(prevOriginalContent, prevSelector, prevBubbleSelector);
					ttsOriginalContentMap.set(currentPlayingElement, null);
				}

				const selector = "#" + el.id;
				const bubbleSelector = `[data-tts-id="${ uniqueId }"]`;

				// Récupère et met à jour le contenu original
				const originalContent = ttsOriginalContentMap.get(el) ?? null;
				const newOriginalContent = speak(originalContent, selector, bubbleSelector);
				ttsOriginalContentMap.set(el, newOriginalContent);

				// Met à jour la référence de l'élément en cours
				currentPlayingElement = newOriginalContent ? el : null;
			});
		},
		unmounted: (el: HTMLElement) =>
		{
			// Nettoie la map quand l'élément est démonté
			ttsOriginalContentMap.delete(el);

			// Si c'est l'élément en cours de lecture, nettoyer
			if (currentPlayingElement === el)
			{
				currentPlayingElement = null;
			}

			// Arrête la lecture si en cours
			if (el.classList.contains("is-playing"))
			{
				window.speechSynthesis.cancel();
			}
		}
	});
};

export default installTtsDirective;
